// Header compression functionality for desktop users only
(function() {
    'use strict';

    // Check if user is on desktop (screen width > 768px)
    function isDesktop() {
        return window.innerWidth > 768;
    }

    // Only apply to desktop users
    if (!isDesktop()) return;

    let lastScrollTop = 0;
    let ticking = false;
    let isNavigationHidden = false;
    let isToolbarHidden = false;
    let isLogoCompressed = false;
    const header = document.getElementById('shopify-section-header_new');
    
    // Ensure header exists and required APIs are available
    if (!header || typeof window.addEventListener !== 'function') return;

    // Scroll thresholds
    const SCROLL_THRESHOLD = 100; // Minimum scroll before hiding navigation
    const TOP_THRESHOLD = 20; // Very top of page
    const SCROLL_DELTA = 10; // Minimum scroll difference to trigger change (prevents jitter)

    function handleScroll() {
        const currentScrollTop = Math.max(
            window.pageYOffset || 0,
            document.documentElement.scrollTop || 0,
            document.body.scrollTop || 0
        );

        // Only apply on desktop
        if (!isDesktop()) return;

        // Calculate scroll difference
        const scrollDifference = Math.abs(currentScrollTop - lastScrollTop);
        
        // Ignore small scroll movements to prevent jitter
        if (scrollDifference < SCROLL_DELTA && currentScrollTop > TOP_THRESHOLD) {
            return;
        }

        // At the very top of the page - show everything
        if (currentScrollTop <= TOP_THRESHOLD) {
            if (isToolbarHidden || isNavigationHidden || isLogoCompressed) {
                showToolbar();
                showNavigation();
                expandLogo();
            }
            lastScrollTop = currentScrollTop;
            return;
        }

        // Past top threshold - hide toolbar and compress logo
        if (currentScrollTop > TOP_THRESHOLD) {
            if (!isToolbarHidden) {
                hideToolbar();
            }
            if (!isLogoCompressed) {
                compressLogo();
            }
        }

        // Handle navigation visibility based on scroll direction
        if (currentScrollTop > SCROLL_THRESHOLD) {
            // Scrolling down - hide navigation
            if (currentScrollTop > lastScrollTop) {
                if (!isNavigationHidden) {
                    hideNavigation();
                }
            }
            // Scrolling up - show navigation
            else if (currentScrollTop < lastScrollTop) {
                if (isNavigationHidden) {
                    showNavigation();
                }
            }
        } else {
            // Below threshold - always show navigation
            if (isNavigationHidden) {
                showNavigation();
            }
        }

        lastScrollTop = currentScrollTop;
    }

    function hideToolbar() {
        if (header && header.classList) {
            header.classList.add('toolbar-hidden');
            isToolbarHidden = true;
        }
    }

    function showToolbar() {
        if (header && header.classList) {
            header.classList.remove('toolbar-hidden');
            isToolbarHidden = false;
        }
    }

    function hideNavigation() {
        if (header && header.classList) {
            header.classList.add('navigation-hidden');
            isNavigationHidden = true;
        }
    }

    function showNavigation() {
        if (header && header.classList) {
            header.classList.remove('navigation-hidden');
            isNavigationHidden = false;
        }
    }

    function compressLogo() {
        if (header && header.classList) {
            header.classList.add('logo-compressed');
            isLogoCompressed = true;
        }
    }

    function expandLogo() {
        if (header && header.classList) {
            header.classList.remove('logo-compressed');
            isLogoCompressed = false;
        }
    }

    // Request animation frame for smooth scrolling
    function requestTick() {
        if (!ticking) {
            window.requestAnimationFrame(function() {
                try {
                    handleScroll();
                } catch (error) {
                    console.warn('Header scroll handler error:', error);
                }
                ticking = false;
            });
            ticking = true;
        }
    }

    // Add scroll listener with requestAnimationFrame for smooth performance
    window.addEventListener('scroll', requestTick, { passive: true });

    // Handle window resize
    window.addEventListener('resize', function() {
        if (!isDesktop()) {
            // If switching to mobile, reset everything
            showToolbar();
            showNavigation();
            expandLogo();
        }
    }, { passive: true });

    // Cleanup function for potential future use
    function cleanup() {
        try {
            window.removeEventListener('scroll', throttledScrollHandler);
            showToolbar();
            showNavigation();
            expandLogo();
        } catch (error) {
            console.warn('Header cleanup error:', error);
        }
    }

    // Make cleanup available globally if needed
    window.headerScrollCleanup = cleanup;
})();

// ========================================
// Navbar Predictive Search (Desktop Only)
// ========================================
(function() {
    'use strict';

    // Check if user is on desktop
    function isDesktop() {
        return window.innerWidth > 768;
    }

    // Only run on desktop
    if (!isDesktop()) return;

    const searchInput = document.getElementById('navbar-search-input');
    const searchDropdown = document.getElementById('navbar-search-dropdown');
    const searchForm = document.getElementById('navbar-search-form');

    if (!searchInput || !searchDropdown || !searchForm) return;

    let searchTimeout;
    let currentQuery = '';

    // Debounce function to limit API calls
    function debounce(func, delay) {
        return function() {
            const context = this;
            const args = arguments;
            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(() => func.apply(context, args), delay);
        };
    }

    // Format money with currency
    function formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
            return Shopify.formatMoney(cents);
        }
        // Fallback format
        const amount = (cents / 100).toFixed(2);
        return 'Dhs. ' + amount;
    }

    // Perform predictive search
    function performSearch(query) {
        if (!query || query.length < 2) {
            hideDropdown();
            return;
        }

        currentQuery = query;
        showLoading();

        // Use Shopify's predictive search API
        fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`)
            .then(response => response.json())
            .then(data => {
                // Only show results if this is still the current query
                if (query === currentQuery) {
                    displayResults(data, query);
                }
            })
            .catch(error => {
                console.error('Search error:', error);
                hideDropdown();
            });
    }

    // Show loading state
    function showLoading() {
        searchDropdown.innerHTML = '<div class="navbar-search-loading">Searching...</div>';
        searchDropdown.classList.add('active');
    }

    // Hide dropdown
    function hideDropdown() {
        searchDropdown.classList.remove('active');
        searchDropdown.innerHTML = '';
    }

    // Display search results
    function displayResults(data, query) {
        const products = data.resources.results.products || [];

        if (products.length === 0) {
            searchDropdown.innerHTML = '<div class="navbar-search-no-results">No products found</div>';
            searchDropdown.classList.add('active');
            return;
        }

        let html = '<div class="navbar-search-results-header">Products</div>';

        products.forEach(product => {
            const imageUrl = product.featured_image 
                ? product.featured_image.url 
                : 'https://cdn.shopify.com/s/files/1/0000/0000/files/placeholder.png';
            
            // Price is in cents, convert to currency format
            const price = formatMoney(product.price);
            
            // Compare at price - check if exists and is higher than current price
            let compareAtPrice = null;
            if (product.compare_at_price_max && product.compare_at_price_max > product.price) {
                compareAtPrice = formatMoney(product.compare_at_price_max);
            }

            html += `
                <a href="${product.url}" class="navbar-search-result-item">
                    <div class="navbar-search-result-image">
                        <img src="${imageUrl}" alt="${product.title}" loading="lazy">
                    </div>
                    <div class="navbar-search-result-info">
                        <div class="navbar-search-result-title">${product.title}</div>
                        <div class="navbar-search-result-price">
                            ${compareAtPrice ? `<span class="compare-price">${compareAtPrice}</span>` : ''}
                            ${price}
                        </div>
                    </div>
                </a>
            `;
        });

        // Add "View All Results" link
        html += `<a href="/search?q=${encodeURIComponent(query)}" class="navbar-search-view-all">View All Results</a>`;

        searchDropdown.innerHTML = html;
        searchDropdown.classList.add('active');
    }

    // Event listeners
    searchInput.addEventListener('input', debounce(function(e) {
        const query = e.target.value.trim();
        performSearch(query);
    }, 300));

    // Handle form submission (Enter key)
    searchForm.addEventListener('submit', function(e) {
        // Let the form submit naturally to /search page
        hideDropdown();
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function(e) {
        if (!searchInput.contains(e.target) && !searchDropdown.contains(e.target)) {
            hideDropdown();
        }
    });

    // Show dropdown again when clicking on input if there's a query
    searchInput.addEventListener('focus', function() {
        if (searchInput.value.trim().length >= 2) {
            performSearch(searchInput.value.trim());
        }
    });

    // Handle window resize
    window.addEventListener('resize', function() {
        if (!isDesktop()) {
            hideDropdown();
        }
    });

})();
